import { getSubscriptionsByEmail } from '../services/stripe';
import { UserData } from '../components/Admin/Stepper/User/UserData';

interface Item {
  allowedSubscriptions: string[];
}

interface SubscriptionStatus {
  subscriptionName: string;
  active: boolean;
}

export const normalizePlanName = (value: unknown): string => String(value || '').trim().toUpperCase();

const getAllowedSubscriptions = (item: Item): string[] => {
  if (!Array.isArray(item?.allowedSubscriptions)) return [];
  return item.allowedSubscriptions.map(normalizePlanName).filter(Boolean);
};

export const getSubscriptionPlanName = (subscription: any): string => {
  return normalizePlanName(
    subscription?.productMetadata?.plan_id ||
    subscription?.planMetadata?.plan_id ||
    subscription?.subscriptionType ||
    subscription?.subscriptionName
  );
};

export async function checkUserMembership(user: UserData): Promise<SubscriptionStatus[]> {
  const subscriptionStatusMap = new Map<string, boolean>();

  try {
    const userPlans = Array.isArray(user?.subscriptionPlanId)
      ? user.subscriptionPlanId.map(normalizePlanName).filter(Boolean)
      : [];

    // Bundle/video purchases are persisted on user.subscriptionPlanId and should be considered active.
    userPlans.forEach((subscriptionName) => {
      subscriptionStatusMap.set(subscriptionName, true);
    });

    if (!user?.email) {
      return Array.from(subscriptionStatusMap.entries()).map(([subscriptionName, active]) => ({
        subscriptionName,
        active,
      }));
    }

    const stripeSubscriptions = await getSubscriptionsByEmail(user.email);

    // Stripe endpoint can temporarily fail and return an empty list from the service.
    // Fallback to user.subscriptions when available to avoid false negatives.
    if ((!stripeSubscriptions || stripeSubscriptions.length === 0) && Array.isArray((user as any)?.subscriptions)) {
      (user as any).subscriptions.forEach((subscription: any) => {
        const planName = normalizePlanName(subscription?.planId || subscription?.subscriptionType);
        if (planName) {
          subscriptionStatusMap.set(planName, true);
        }
      });
    }

    stripeSubscriptions.forEach((subscription: any) => {
      const planName = getSubscriptionPlanName(subscription);
      if (!planName) return;

      const isActive = normalizePlanName(subscription?.status) === 'ACTIVE';
      subscriptionStatusMap.set(planName, Boolean(subscriptionStatusMap.get(planName)) || isActive);
    });
  } catch (error) {
    console.error('Error checking user membership:', error);
  }

  return Array.from(subscriptionStatusMap.entries()).map(([subscriptionName, active]) => ({
    subscriptionName,
    active,
  }));
}

export const checkAllowUser = (
  item: Item,
  currentUser: { subscriptionPlanId?: string[] },
  subcriptionUserList: SubscriptionStatus[] | undefined
) => {
  const allowedSubscriptions = getAllowedSubscriptions(item);

  if (allowedSubscriptions.includes('FREE')) {
    return true;
  }

  const activePlans = new Set(
    (subcriptionUserList || [])
      .filter((subscriptionStatus) => subscriptionStatus.active)
      .map((subscriptionStatus) => normalizePlanName(subscriptionStatus.subscriptionName))
      .filter(Boolean)
  );

  if (activePlans.size === 0 && Array.isArray(currentUser?.subscriptionPlanId)) {
    currentUser.subscriptionPlanId
      .map(normalizePlanName)
      .filter(Boolean)
      .forEach((planName) => activePlans.add(planName));
  }

  return allowedSubscriptions.some((allowedSubscription) => activePlans.has(allowedSubscription));
};